/**
 * Kickard PHASE 5.0B — Match Result Premium (Ranked, Normal + Penalty).
 *
 * ONE shared component for both normal-ranked victory and penalty-ranked
 * victory. The variant swap is the score panel asset + the penalty chip
 * + two extra live PENS values. Everything after the score panel is
 * identical for both variants.
 *
 * PRESENTATION ONLY. Reuses the EXACT existing production data pipelines
 * and routes:
 *
 *   • Ranked result  → `getRankedResult(matchId, guestId)` (same cadence
 *                      as CompetitiveResultBlock)
 *   • Weekly Cup     → `fetchWeeklyCupMatchContribution(matchId, guestId)`
 *   • Rank crest     → production TIER_ART map (HomeScreen) + roman numeral
 *   • Weekly trophy  → production `/kickard/weekly-cup-trophy.webp`
 *   • Preseason/Table route (whole-card tap on Ranked panel) → `/ranked/leaderboard`
 *   • Weekly Cup Table route (whole-card tap on Weekly panel) → `/ranked/weekly-cup`
 *   • Queue Again    → `/ranked/search` (same path Play Hub uses)
 *   • Luck Report    → parent-supplied origin-aware opener
 *   • Home           → parent-supplied `onHome`
 *
 * ZERO rank/trophy art is imported from the ZIP's `reference/` folder.
 * ZIP reference files are NEVER referenced at runtime.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ZONES from './matchResultLiveZonesV3.json';
import { getRankedResult } from '@/api/rankedClient';
import { fetchWeeklyCupMatchContribution } from '@/api/weeklyCupClient';
import { track, EVT } from '@/lib/telemetry';
import { TIER_ART, crestNumeralStyle, CREST_PLACEHOLDER_STYLE } from '../lib/rankCrest';

const BG      = '/match_result_premium/background/match_result_stadium_4x.webp';
const LOGO    = '/match_result_premium/brand/kickard_logo_existing.png';
const TITLES  = {
  victory: '/match_result_premium/brand/title_victory_4x.png',
  defeat:  '/match_result_premium/brand/title_defeat_4x.png',
  draw:    '/match_result_premium/brand/title_draw_4x.png',
};
const PANEL = {
  score_normal:  '/match_result_premium/panels/score_normal_4x.png',
  score_penalty: '/match_result_premium/panels/score_penalty_4x.png',
  penalty_chip:  '/match_result_premium/panels/penalty_chip_4x.png',
  ranked:        '/match_result_premium/panels/ranked_panel_4x.png',
  weekly:        '/match_result_premium/panels/weekly_panel_4x.png',
  h2h:           '/match_result_premium/panels/h2h_panel_4x.png',
};
// Ranked VICTORY Premium Gold V3 — decorative-only assets (victory only).
const V3 = '/match_result_premium/victory_v3';
const V3_VARS = {
  '--mrv3-beams-l': `url(${V3}/left_championship_beams.webp)`,
  '--mrv3-beams-r': `url(${V3}/right_championship_beams.webp)`,
  '--mrv3-clouds': `url(${V3}/championship_clouds.webp)`,
  '--mrv3-metal': `url(${V3}/metal_smoke_surface.webp)`,
};
// Premium Crimson DEFEAT V5 — decorative-only hero (static art, no match data).
const V5_HERO = '/match_result_premium/defeat_v5/defeat_hero_crimson.webp';
const CTA_BLANK = '/match_result_premium/buttons/cta_gold_blank_4x.png';
const Sheen = () => <span className="mrv3-sheen" aria-hidden />;
const Outline = () => <span className="mrv3-outline" aria-hidden />;

const BTN = {
  queue: '/match_result_premium/buttons/queue_again_4x.png',
  luck:  '/match_result_premium/buttons/view_luck_report_4x.png',
  home:  '/match_result_premium/buttons/home_4x.png',
};

// Live production rank crest mapping — DUPLICATED verbatim from
// HomeScreen (same precedent as Scoreboard.jsx). Do NOT substitute.
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };
// Live production Weekly trophy — same source as HomeScreen (WEEKLY_TROPHY).
const WEEKLY_TROPHY = '/kickard/weekly-cup-trophy.webp';

const RANKED_POLL_INTERVAL_MS = 1000;
const MAX_RANKED_POLLS = 12;

const COLOR = {
  gold:  '#FFE269',
  red:   '#F82F34',
  green: '#5BECBA',
  cream: '#F6F4EE',
  mute:  '#BEBCB4',
};

function tierLabel(tier, division) {
  if (!tier || tier === 'unranked') return 'UNRANKED';
  if (tier === 'legend') return 'LEGEND';
  const name = String(tier).toUpperCase();
  const div = DIVISION_ROMAN[Number(division) || 0];
  return div ? `${name} ${div}` : name;
}

/** Live-field positioned absolutely inside its parent panel container (px coords from the ref grid). */
function Z({ zone, align = zone.align || 'left', color, children, testid, bold = true }) {
  if (!zone) return null;
  const style = {
    position: 'absolute',
    left: zone.x, top: zone.y, width: zone.w, height: zone.h,
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
    color: color || (zone.color && COLOR[zone.color]) || COLOR.cream,
    fontFamily: "'Oswald','Impact',sans-serif",
    fontWeight: bold ? 700 : 500,
    fontSize: zone.fontPx || 14,
    lineHeight: 1,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    letterSpacing: '0.02em',
  };
  return <div data-testid={testid} style={style}>{children}</div>;
}

export default function MatchResultPremium({
  state,
  matchId,
  myGuestId,
  myPlayerId = 'p1',
  myName,
  opponentName,
  h2h,
  h2hUpdating,
  isWin,
  isTie,
  reason,            // 'penalty' | 'forfeit' | null
  onViewLuckReport,
  onHome,
  mode = 'ranked',   // 'ranked' | 'quick' | 'friend' | 'bot'
  subtitle = null,   // non-ranked: existing production outcome sentence
  primaryAction = null, // non-ranked: mode's own primary control (Rematch)
  showLuckReport = true,
}) {
  const isRanked = mode === 'ranked';
  const navigate = useNavigate();
  const [launching, setLaunching] = useState(false);

  // ------------- live Ranked / Weekly fetch (same cadence as CompetitiveResultBlock) -------------
  const [rankedResult, setRankedResult] = useState(null);
  const [weeklyContribution, setWeeklyContribution] = useState(null);
  const [rankedTries, setRankedTries] = useState(0);
  const [weeklyTries, setWeeklyTries] = useState(0);
  const eligible = isRanked && !!matchId && !!myGuestId;

  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    (async () => {
      const r = await getRankedResult(matchId, myGuestId);
      if (cancelled) return;
      setRankedResult(r);
      if (r?.ok && r.status === 'pending' && rankedTries < MAX_RANKED_POLLS) {
        setTimeout(() => { if (!cancelled) setRankedTries((n) => n + 1); }, RANKED_POLL_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, matchId, myGuestId, rankedTries]);

  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    (async () => {
      const r = await fetchWeeklyCupMatchContribution(matchId, myGuestId);
      if (cancelled) return;
      setWeeklyContribution(r);
      if (r?.ok && r.matchFound === false && weeklyTries < MAX_RANKED_POLLS) {
        setTimeout(() => { if (!cancelled) setWeeklyTries((n) => n + 1); }, RANKED_POLL_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, matchId, myGuestId, weeklyTries]);

  // ------------- Field derivations (ALL from existing production data) -------------
  const me = state?.players?.[myPlayerId] || {};
  const oppKey = myPlayerId === 'p1' ? 'p2' : 'p1';
  const opp = state?.players?.[oppKey] || {};
  const meName = (myName || me.name || 'You').toUpperCase();
  const oppNameUp = (opponentName || opp.name || 'Opponent').toUpperCase();
  const myReg = me.score ?? 0;
  const oppReg = opp.score ?? 0;
  const isPenalty = reason === 'penalty' && !!state?.penalty?.scores;
  const myPen = isPenalty ? (state.penalty.scores[myPlayerId] ?? 0) : null;
  const oppPen = isPenalty ? (state.penalty.scores[oppKey] ?? 0) : null;
  const goalsScored = me.score ?? 0;
  const goalsConceded = opp.score ?? 0;
  const attacks = me.attacksPerformed ?? 0;
  const saves = me.savesPerformed ?? 0;

  const outcomeKey = isTie ? 'draw' : isWin ? 'victory' : 'defeat';
  const isForfeit = reason === 'forfeit';
  // Outcome sentence uses the existing production semantics:
  //   - penalty finish: "You beat X on penalties" / "X won on penalties"
  //   - forfeit finish: "You won by timeout" / "X won by timeout" (same
  //     language as the existing production stadium-bg summary subtitle)
  //   - normal finish:  "You defeated X" / "X won"
  //   - draw:           "A close call" (only shown if the current
  //     product genuinely finalizes a Ranked draw)
  const outcomeSentence = !isRanked ? subtitle : isTie
    ? 'A close call'
    : isWin
      ? (isPenalty ? `You beat ${opponentName || opp.name || 'Opponent'} on penalties`
                   : isForfeit ? `You won by timeout`
                   : `You defeated ${opponentName || opp.name || 'Opponent'}`)
      : (isPenalty ? `${opponentName || opp.name || 'Opponent'} won on penalties`
                   : isForfeit ? `${opponentName || opp.name || 'Opponent'} won by timeout`
                   : `${opponentName || opp.name || 'Opponent'} won`);

  // Ranked payload fields (same derivation as CompetitiveResultBlock).
  const p = rankedResult?.player || {};
  const srDelta = Number(p.srDelta || 0);
  const rankedReady = rankedResult?.status === 'processed';
  const srDeltaText = rankedReady ? (srDelta > 0 ? `+${srDelta} SR` : `${srDelta} SR`) : '…';
  const srDeltaColor = srDelta > 0 ? COLOR.green : srDelta < 0 ? COLOR.red : COLOR.cream;
  const afterTier = p.afterTier || 'unranked';
  const afterDivision = p.afterDivision;
  const tierDivisionText = rankedReady ? tierLabel(afterTier, afterDivision) : '…';
  const srTotalText = rankedReady ? `${Number(p.afterSR || 0)} SR` : '';
  const crestSrc = TIER_ART[afterTier] || TIER_ART.gold;
  const crestIsPlaceholder = !TIER_ART[afterTier];
  const crestNumeral = rankedReady && afterTier !== 'legend' && afterTier !== 'unranked' && afterDivision
    ? DIVISION_ROMAN[Number(afterDivision)] : null;
  const rankProgressPct = Math.max(0, Math.min(1, Number(p.progressWithinTier ?? 0)));

  // Weekly payload fields.
  const wc = weeklyContribution || {};
  const weeklyReady = wc.ok && wc.matchFound !== false;
  const weeklyPts = Number(wc.pointsEarned || 0);
  const weeklyMatches = Number(wc.countedMatches || 0);
  const weeklyMax = Number(wc.maxCountedMatches || 15);
  const weeklyCounted = wc.counted === true;
  const weeklyPtsText = weeklyReady ? `+${weeklyPts} ${weeklyPts === 1 ? 'PT' : 'PTS'}` : '…';
  const weeklyCountText = weeklyReady ? `${weeklyMatches} / ${weeklyMax}` : '';
  const weeklyProgressPct = weeklyMax > 0 ? weeklyMatches / weeklyMax : 0;

  // H2H derivation. loading/updating/null → ellipsis only, never fake 0-0 / FIRST MEETING.
  const h2hLoading = !h2h || h2hUpdating === true;
  const h2hMyWins = h2h?.myWins ?? 0;
  const h2hOppWins = h2h?.opponentWins ?? 0;
  const h2hMyPen = h2h?.myPenaltyWins ?? 0;
  const h2hOppPen = h2h?.opponentPenaltyWins ?? 0;
  const h2hMyGoals = h2h?.myGoals ?? 0;
  const h2hOppGoals = h2h?.opponentGoals ?? 0;
  const h2hTotal = h2hMyWins + h2hOppWins;
  const dispMe = (myName || me.name || 'You').toUpperCase();
  const dispOpp = (opponentName || opp.name || 'Opponent').toUpperCase();
  const formatWins = (w, pw) => `${w} (${pw}P)`;
  let h2hSummary;
  if (h2hLoading) h2hSummary = '…';
  else if (h2hTotal === 0) h2hSummary = 'FIRST MEETING';
  else if (h2hMyWins > h2hOppWins) h2hSummary = `${dispMe} LEADS ${formatWins(h2hMyWins, h2hMyPen)} – ${formatWins(h2hOppWins, h2hOppPen)}`;
  else if (h2hOppWins > h2hMyWins) h2hSummary = `${dispOpp} LEADS ${formatWins(h2hOppWins, h2hOppPen)} – ${formatWins(h2hMyWins, h2hMyPen)}`;
  else h2hSummary = `ALL SQUARE ${formatWins(h2hMyWins, h2hMyPen)} – ${formatWins(h2hOppWins, h2hOppPen)}`;
  const h2hGoalsText = h2hLoading ? '…' : (h2hTotal > 0 ? `GOALS ${h2hMyGoals}–${h2hOppGoals}` : '');

  // Save a canonical match-summary snapshot to sessionStorage so Back
  // from Ranked table / Weekly Cup table can restore the SAME finalized
  // Match Result via the dedicated `/match-summary/:matchId` route —
  // even after the live WebSocket / room has closed. This mirrors the
  // Luck Report snapshot flow in MatchSummaryView. READ-ONLY: never
  // re-finalizes, never re-emits SR / Weekly, never re-arms rematch.
  //
  // IMPORTANT: `h2h` and `h2hUpdating` are included so the restored
  // Match Result shows the SAME head-to-head values the player saw
  // live. Without these the restored view would default to loading
  // `…` indefinitely (safe) but never render the resolved stats.
  const saveMatchSummarySnapshot = () => {
    if (!matchId) return;
    try {
      const snapshot = {
        state,
        myPlayerId,
        isOnline: true,
        opponentName,
        matchType: 'ranked',
        myName,
        myGuestId,
        h2h,
        h2hUpdating,
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(
        `kickard.summary.${matchId}`,
        JSON.stringify(snapshot),
      );
    } catch (_) { /* quota / private mode */ }
  };

  // Keep the persisted snapshot in sync whenever H2H resolves /
  // updates. This covers the window where the user leaves the Match
  // Result after H2H arrives asynchronously: the snapshot always
  // contains the latest resolved H2H the user has actually seen.
  useEffect(() => {
    if (!matchId || !isRanked) return;
    try {
      const existingRaw = sessionStorage.getItem(`kickard.summary.${matchId}`);
      // Only update if a snapshot already exists for this match (i.e.
      // the user has at least once opened a nav path that persisted
      // it) OR if H2H has resolved to a real payload worth saving.
      const existing = existingRaw ? JSON.parse(existingRaw) : null;
      if (!existing && !h2h) return;
      const snapshot = {
        state,
        myPlayerId,
        isOnline: true,
        opponentName,
        matchType: 'ranked',
        myName,
        myGuestId,
        h2h,
        h2hUpdating,
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(
        `kickard.summary.${matchId}`,
        JSON.stringify(snapshot),
      );
    } catch (_) { /* quota / private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, h2h, h2hUpdating]);

  // ------------- Handlers -------------
  const onQueueAgain = () => {
    if (launching) return;
    setLaunching(true);
    try { track(EVT.QUEUE_AGAIN_CLICKED, { metadata: { context: 'ranked_queue_again', matchId, outcome: isTie ? 'tie' : isWin ? (isPenalty ? 'win_penalty' : isForfeit ? 'win_forfeit' : 'win_normal') : (isPenalty ? 'loss_penalty' : isForfeit ? 'loss_forfeit' : 'loss_normal') } }); } catch (_) { /* ignore */ }
    navigate('/ranked/search');
  };
  const openRankedTable = () => {
    try { track(EVT.SUMMARY_VIEWED, { metadata: { action: 'open_ranked_table', matchId } }); } catch (_) { /* ignore */ }
    saveMatchSummarySnapshot();
    navigate('/ranked/leaderboard', {
      state: matchId ? {
        origin: 'match_result',
        matchId,
        returnPath: `/match-summary/${encodeURIComponent(matchId)}`,
      } : undefined,
    });
  };
  const openWeeklyTable = () => {
    try { track(EVT.SUMMARY_VIEWED, { metadata: { action: 'open_weekly_table', matchId } }); } catch (_) { /* ignore */ }
    saveMatchSummarySnapshot();
    navigate('/ranked/weekly-cup', {
      state: matchId ? {
        origin: 'match_result',
        matchId,
        returnPath: `/match-summary/${encodeURIComponent(matchId)}`,
      } : undefined,
    });
  };

  // ------------- Zone maps -------------
  // Victory = gold V3 art; Defeat = crimson V5 hero. Both share the
  // polished V3 panel/button chrome (sheen, 2px outline, button rims).
  const skin = outcomeKey === 'victory' || outcomeKey === 'defeat' ? outcomeKey : null;
  const v3 = !!skin;
  const vic = skin === 'victory';
  const scoreZones = isPenalty ? ZONES.penaltyScore : ZONES.normalScore;
  const scoreAsset = isPenalty ? PANEL.score_penalty : PANEL.score_normal;
  const scoreSize = scoreZones.cssSize;
  const rankedZones = ZONES.ranked.liveZones;
  const weeklyZones = ZONES.weekly.liveZones;
  const h2hZones = ZONES.h2h.liveZones;
  const rankedSize = ZONES.ranked.cssSize;
  const weeklySize = ZONES.weekly.cssSize;
  const h2hSize = ZONES.h2h.cssSize;

  return (
    <div
      data-testid="match-result-premium"
      className={vic ? 'mrv3' : v3 ? 'mrv3 mrv3--defeat' : undefined}
      data-result-mode={mode}
      style={{
        ...(vic ? V3_VARS : null),
        ...(skin === 'defeat' ? { '--mrv3-metal': V3_VARS['--mrv3-metal'], '--mrv5-hero': `url(${V5_HERO})` } : null),
        position: 'relative',
        width: '100%',
        minHeight: '100dvh',
        background: v3 ? undefined : `linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.86) 60%, rgba(0,0,0,0.94) 100%), url(${BG}) center top / cover no-repeat #000`,
        padding: '20px 16px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {skin === 'defeat' && (
        <>
          <span className="mrv3-fx mrv5-glow" aria-hidden />
          <img src={V5_HERO} alt="" aria-hidden draggable={false} className="mrv5-hero" data-testid="mrv5-hero-art" />
        </>
      )}
      {vic && (
        <>
          <span className="mrv3-fx mrv3-fx--glow" aria-hidden />
          <span className="mrv3-fx mrv3-fx--beams-l" aria-hidden />
          <span className="mrv3-fx mrv3-fx--beams-r" aria-hidden />
          <span className="mrv3-fx mrv3-fx--clouds" aria-hidden />
          <img src={`${V3}/victory_crown_laurels.webp`} alt="" aria-hidden draggable={false} className="mrv3-title-art" data-testid="mrv3-title-art" />
        </>
      )}
      {/* Hero */}
      <img src={LOGO} alt="Kickard" draggable={false} style={{ width: 132, height: 'auto', marginBottom: 10, ...(skin === 'defeat' ? { opacity: 0 } : null) }} />
      <img
        src={TITLES[outcomeKey]}
        alt={outcomeKey.toUpperCase()}
        draggable={false}
        style={{ width: 330, maxWidth: '100%', height: 'auto', marginBottom: 6, ...(v3 ? { opacity: 0 } : null) }}
      />
      <div
        data-testid="mr-outcome-sentence"
        style={{
          color: COLOR.cream, fontFamily: "'Manrope',sans-serif", fontSize: 14,
          textAlign: 'center', maxWidth: 330, marginBottom: 12,
        }}
      >
        {outcomeSentence}
      </div>

      {/* Penalty chip above score panel (only in penalty variant) */}
      {isPenalty && (
        <img
          src={PANEL.penalty_chip}
          alt="Decided on penalties"
          draggable={false}
          style={{ height: 24, width: 'auto', marginBottom: 8 }}
        />
      )}

      {/* Score panel — shared container, variant swap by asset + zones */}
      <div style={{ position: 'relative', width: scoreSize[0], maxWidth: '100%', aspectRatio: `${scoreSize[0]} / ${scoreSize[1]}`, marginBottom: 14 }}>
        <img src={scoreAsset} alt="" aria-hidden draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        {v3 && <Sheen />}
        <Z zone={scoreZones.liveZones.leftName}  align="center" color={COLOR.cream} testid="mr-left-name"  bold>{meName}</Z>
        <Z zone={scoreZones.liveZones.rightName} align="center" color={COLOR.cream} testid="mr-right-name" bold>{oppNameUp}</Z>
        <Z zone={scoreZones.liveZones.leftScore}  align="center" color={COLOR.gold} testid="mr-left-score">{myReg}</Z>
        <Z zone={scoreZones.liveZones.rightScore} align="center" color={COLOR.red}  testid="mr-right-score">{oppReg}</Z>
        {isPenalty && (
          <>
            <Z zone={scoreZones.liveZones.leftPens}  align="center" color={COLOR.gold} testid="mr-left-pens">{myPen}</Z>
            <Z zone={scoreZones.liveZones.rightPens} align="center" color={COLOR.red}  testid="mr-right-pens">{oppPen}</Z>
          </>
        )}
        <Z zone={scoreZones.liveZones.goalsScoredValue}   align="right" color={COLOR.cream} testid="mr-goals-scored">{goalsScored}</Z>
        <Z zone={scoreZones.liveZones.goalsConcededValue} align="right" color={COLOR.cream} testid="mr-goals-conceded">{goalsConceded}</Z>
        <Z zone={scoreZones.liveZones.attacksValue}       align="right" color={COLOR.cream} testid="mr-attacks">{attacks}</Z>
        <Z zone={scoreZones.liveZones.savesValue}         align="right" color={COLOR.cream} testid="mr-saves">{saves}</Z>
        {v3 && <Outline />}
      </div>

      {/* Ranked + Weekly row (Ranked only) */}
      {isRanked && (
      <div style={{ display: 'flex', gap: 12, width: 358, maxWidth: '100%', marginBottom: 14 }}>
        {/* Ranked panel — WHOLE CARD TAP → Preseason/Table */}
        <button
          type="button"
          onClick={openRankedTable}
          aria-label="Open Ranked leaderboard"
          data-testid="mr-ranked-card"
          style={{
            position: 'relative', flex: 1, aspectRatio: `${rankedSize[0]} / ${rankedSize[1]}`,
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <img src={PANEL.ranked} alt="" aria-hidden draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          {v3 && <Sheen />}
          <Z zone={rankedZones.srDelta} align="left" color={srDeltaColor} testid="mr-sr-delta">{srDeltaText}</Z>
          {/* Live rank crest (production TIER_ART, with roman numeral) */}
          <div data-testid="mr-rank-crest" style={{ position: 'absolute', left: rankedZones.rankCrest.x, top: rankedZones.rankCrest.y, width: rankedZones.rankCrest.w, height: rankedZones.rankCrest.h, pointerEvents: 'none' }}>
            {rankedReady && (
              <>
                <img src={crestSrc} alt="" aria-hidden draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.55))', ...(crestIsPlaceholder ? CREST_PLACEHOLDER_STYLE : null) }} />
                {crestNumeral && (
                  <span aria-hidden data-testid="mr-rank-numeral" style={{ ...crestNumeralStyle(afterTier), color: '#fff', fontFamily: "'Oswald',sans-serif", fontWeight: 700, fontSize: 14, textShadow: '0 1px 0 rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.9)' }}>
                    {crestNumeral}
                  </span>
                )}
              </>
            )}
          </div>
          <Z zone={rankedZones.tierDivision} align="left" color={COLOR.cream} testid="mr-tier-division">{tierDivisionText}</Z>
          {rankedReady && <Z zone={rankedZones.srTotal} align="right" color={COLOR.cream} testid="mr-sr-total">{srTotalText}</Z>}
          {rankedReady && (
            <div aria-hidden data-testid="mr-rank-progress-fill" style={{
              position: 'absolute',
              left: rankedZones.progressFill.x, top: rankedZones.progressFill.y,
              width: rankedZones.progressFill.w * rankProgressPct,
              height: rankedZones.progressFill.h,
              background: `linear-gradient(90deg, ${COLOR.gold}dd, ${COLOR.gold})`,
              borderRadius: 2,
              boxShadow: `0 0 6px ${COLOR.gold}`,
            }} />
          )}
          {v3 && <Outline />}
        </button>

        {/* Weekly panel — WHOLE CARD TAP → Weekly Cup Table */}
        <button
          type="button"
          onClick={openWeeklyTable}
          aria-label="Open Weekly Cup leaderboard"
          data-testid="mr-weekly-card"
          style={{
            position: 'relative', flex: 1, aspectRatio: `${weeklySize[0]} / ${weeklySize[1]}`,
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <img src={PANEL.weekly} alt="" aria-hidden draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          {v3 && <Sheen />}
          {/* Live production Weekly trophy — same source as HomeScreen */}
          <img
            src={WEEKLY_TROPHY}
            alt=""
            aria-hidden
            draggable={false}
            data-testid="mr-weekly-trophy"
            style={{
              position: 'absolute',
              left: weeklyZones.trophy.x, top: weeklyZones.trophy.y,
              width: weeklyZones.trophy.w, height: weeklyZones.trophy.h,
              objectFit: 'contain',
              filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.55))',
              pointerEvents: 'none',
            }}
          />
          <Z zone={weeklyZones.points} align="center" color={COLOR.gold} testid="mr-weekly-points">{weeklyPtsText}</Z>
          {weeklyReady && <Z zone={weeklyZones.count} align="right" color={COLOR.cream} testid="mr-weekly-count">{weeklyCountText}</Z>}
          {weeklyReady && weeklyCounted && (
            <div aria-hidden data-testid="mr-weekly-progress-fill" style={{
              position: 'absolute',
              left: weeklyZones.progressFill.x, top: weeklyZones.progressFill.y,
              width: weeklyZones.progressFill.w * weeklyProgressPct,
              height: weeklyZones.progressFill.h,
              background: `linear-gradient(90deg, ${COLOR.green}dd, ${COLOR.green})`,
              borderRadius: 2,
              boxShadow: `0 0 6px ${COLOR.green}`,
            }} />
          )}
          {v3 && <Outline />}
        </button>
      </div>
      )}

      {/* H2H panel (Ranked always; Quick/Friend when a rivalry record exists) */}
      {(isRanked || (mode !== 'bot' && (h2h || h2hUpdating))) && (
      <div data-testid="mr-h2h-card" style={{ position: 'relative', width: h2hSize[0], maxWidth: '100%', aspectRatio: `${h2hSize[0]} / ${h2hSize[1]}`, marginBottom: 14 }}>
        <img src={PANEL.h2h} alt="" aria-hidden draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        {v3 && <Sheen />}
        <Z zone={h2hZones.leftWins}         align="center" color={COLOR.gold} testid="mr-h2h-left-wins">{h2hLoading ? '…' : h2hMyWins}</Z>
        <Z zone={h2hZones.leftPenaltyWins}  align="left"   color={COLOR.mute} testid="mr-h2h-left-pen"  bold={false}>{h2hLoading ? '' : `(${h2hMyPen}P)`}</Z>
        <Z zone={h2hZones.rightWins}        align="center" color={COLOR.red}  testid="mr-h2h-right-wins">{h2hLoading ? '…' : h2hOppWins}</Z>
        <Z zone={h2hZones.rightPenaltyWins} align="right"  color={COLOR.mute} testid="mr-h2h-right-pen" bold={false}>{h2hLoading ? '' : `(${h2hOppPen}P)`}</Z>
        <Z zone={h2hZones.leftName}  align="center" color={COLOR.mute} testid="mr-h2h-left-name"  bold={false}>{dispMe}</Z>
        <Z zone={h2hZones.rightName} align="center" color={COLOR.mute} testid="mr-h2h-right-name" bold={false}>{dispOpp}</Z>
        <Z zone={h2hZones.summary} align="center" color={COLOR.cream} testid="mr-h2h-summary">{h2hSummary}</Z>
        {h2hGoalsText && <Z zone={h2hZones.goals} align="left" color={COLOR.mute} testid="mr-h2h-goals" bold={false}>{h2hGoalsText}</Z>}
        {v3 && <Outline />}
      </div>
      )}

      {/* Actions — Queue Again (primary), Luck Report, Home */}
      {!isRanked && primaryAction && (
        <div data-testid="mr-primary-action" className="mr-primary" style={{ width: 358, maxWidth: '100%', marginBottom: 10 }}>
          {primaryAction}
        </div>
      )}
      {isRanked && (
      <button
        type="button"
        onClick={onQueueAgain}
        disabled={launching}
        aria-busy={launching}
        aria-label="Queue again for a Ranked match"
        data-testid="summary-ranked-queue-again-btn"
        className={v3 ? 'mrv3-btn mrv3-btn--primary' : undefined}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: launching ? 'progress' : 'pointer',
          width: 358, maxWidth: '100%', marginBottom: 10, display: 'block',
          opacity: launching ? 0.7 : 1,
        }}
      >
        <img src={BTN.queue} alt="Queue Again" draggable={false} style={{ width: '100%', height: 'auto', display: 'block' }} />
      </button>
      )}
      <div style={{ display: 'flex', gap: 12, width: 358, maxWidth: '100%', ...(showLuckReport ? null : { justifyContent: 'center' }) }}>
        {showLuckReport && (
        <button
          type="button"
          onClick={onViewLuckReport}
          aria-label="View Luck Report"
          data-testid="summary-luck-report-btn"
          className={v3 ? 'mrv3-btn' : undefined}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flex: 1 }}
        >
          <img src={BTN.luck} alt="View Luck Report" draggable={false} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </button>
        )}
        <button
          type="button"
          onClick={onHome}
          aria-label="Return to Home"
          data-testid="summary-home-btn"
          className={v3 ? 'mrv3-btn' : undefined}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flex: showLuckReport ? 1 : '0 1 calc(50% - 6px)' }}
        >
          <img src={BTN.home} alt="Home" draggable={false} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </button>
      </div>
    </div>
  );
}

/** Gold primary CTA for non-ranked modes: Queue Again button material, live label. */
export function PremiumCta({ label, onClick, testid, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={testid}
      className="mrv3-btn mrv3-btn--primary mr-cta"
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: disabled ? 'progress' : 'pointer', width: '100%', display: 'block', position: 'relative', opacity: disabled ? 0.7 : 1 }}
    >
      <img src={CTA_BLANK} alt="" aria-hidden draggable={false} style={{ width: '100%', height: 'auto', display: 'block' }} />
      <span className="mr-cta-label">
        <svg viewBox="0 0 10 12" aria-hidden className="mr-cta-icon"><path d="M0 0L10 6L0 12Z" /></svg>
        {label}
      </span>
    </button>
  );
}
